import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CLOCK_SYNC_INTERVAL_MS,
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  type SessionCreated,
} from '@sincronia/shared';
import { LobbyView } from '../components/lobby/LobbyView.js';
import { GameView } from '../components/game/GameView.js';
import { Button, Field, Panel, Spinner } from '../components/ui.js';
import { BrandMark } from '../components/Brand.js';
import { errorKey, useT } from '../i18n/index.js';
import { emitAck, getSocket, syncClock } from '../net/socket.js';
import { clearSession, lastUsedName, loadSession, saveSession } from '../lib/session.js';
import { selectIsHost, useGameStore } from '../store/gameStore.js';
import { normalizeCode } from './JoinPage.js';
import { unlockAudio } from '../lib/feedback.js';

type Phase = 'connecting' | 'needsName' | 'ready';

export function RoomPage(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams();
  const code = normalizeCode(params.codigo ?? '');

  const snapshot = useGameStore((state) => state.snapshot);
  const privateState = useGameStore((state) => state.privateState);
  const connection = useGameStore((state) => state.connection);
  const isHost = useGameStore(selectIsHost);
  const setSession = useGameStore((state) => state.setSession);
  const reset = useGameStore((state) => state.reset);

  const [phase, setPhase] = useState<Phase>('connecting');
  const [name, setName] = useState(() => lastUsedName());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const attemptedRef = useRef(false);

  const attemptReconnect = useCallback(async (): Promise<void> => {
    const stored = loadSession(code);
    if (!stored) {
      setPhase('needsName');
      return;
    }

    const response = await emitAck<SessionCreated>('room:reconnect', {
      code,
      playerId: stored.playerId,
      sessionToken: stored.sessionToken,
    });

    if (!response.ok) {
      clearSession(code);
      reset();
      setPhase('needsName');
      if (response.error.code !== 'ROOM_NOT_FOUND') setError(t(errorKey(response.error.code)));
      return;
    }

    setSession(response.data);
    setPhase('ready');
    void syncClock();
  }, [code, reset, setSession, t]);

  /* Conecta e recupera a sessão — inclusive depois de um F5. */
  useEffect(() => {
    if (!code) {
      void navigate('/entrar', { replace: true });
      return;
    }

    const socket = getSocket();
    const run = (): void => {
      attemptedRef.current = true;
      void attemptReconnect();
    };

    if (socket.connected) run();
    socket.on('connect', run);
    return () => {
      socket.off('connect', run);
    };
  }, [code, attemptReconnect, navigate]);

  /* Reajuste periódico do relógio. */
  useEffect(() => {
    const handle = window.setInterval(() => {
      void syncClock(3);
    }, CLOCK_SYNC_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, []);

  useEffect(() => {
    if (snapshot) setPhase('ready');
  }, [snapshot]);

  async function handleJoin(event: FormEvent): Promise<void> {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < NICKNAME_MIN_LENGTH || busy) return;

    unlockAudio();
    setBusy(true);
    setError(null);

    const response = await emitAck<SessionCreated>('room:join', { code, name: trimmed });
    setBusy(false);

    if (!response.ok) {
      setError(t(errorKey(response.error.code)));
      return;
    }

    saveSession(response.data, trimmed);
    setSession(response.data);
    setPhase('ready');
    void syncClock();
  }

  if (phase === 'needsName') {
    return (
      <main className="safe-x safe-bottom safe-top mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-6 px-5 py-8">
        <header className="flex flex-col items-center gap-3 text-center">
          <BrandMark size={56} />
          <h1 className="text-2xl font-semibold text-night-200">{t('join.title')}</h1>
          <p className="tabular text-3xl font-bold tracking-[0.3em] text-pulse-400">{code}</p>
        </header>

        <form className="flex flex-col gap-4" onSubmit={(event) => void handleJoin(event)}>
          <Panel>
            <Field
              label={t('join.nickname')}
              placeholder={t('create.nicknamePlaceholder')}
              value={name}
              maxLength={NICKNAME_MAX_LENGTH}
              autoComplete="nickname"
              onChange={(event) => setName(event.target.value)}
              data-testid="room-join-name"
              required
              autoFocus
            />
          </Panel>

          {error ? (
            <p role="alert" className="rounded-2xl bg-life/10 p-3 text-sm text-life">
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={busy}
            disabled={name.trim().length < NICKNAME_MIN_LENGTH}
            data-testid="room-join-submit"
          >
            {t('join.submit')}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => void navigate('/')}>
            {t('common.back')}
          </Button>
        </form>
      </main>
    );
  }

  if (!snapshot) {
    return (
      <main className="flex flex-1 flex-col items-center justify-center gap-4">
        <BrandMark size={64} />
        <p className="flex items-center gap-2 text-night-300">
          <Spinner />
          {connection === 'connected' ? t('common.loading') : t('common.connecting')}
        </p>
      </main>
    );
  }

  if (snapshot.phase === 'LOBBY') {
    return <LobbyView snapshot={snapshot} privateState={privateState} isHost={isHost} />;
  }

  return <GameView snapshot={snapshot} privateState={privateState} isHost={isHost} />;
}
